import { computed } from 'vue';
import { useLanguageStore } from '@/stores/language';
import { useAuthStore } from '@/stores/auth';
import { getRequiredPermissionFor } from '@/router/routePermissions.js';

const PROJECT_ROLES = [
    'Project Manager',
    'Project Owner',
    'Project Sponsor',
    'Project Council',
    'Project Board',
]

const PROGRAM_ROLES = [
    'Program Manager',
    'Program Council',
    'Program Council · Chairperson Role',
    'Program Council · Secretary Role',
    'Program Owner',
    'Program Director',
    'Program Sponsor',
]

const STRATEGY_ROLES = [
    'SMO',
    'SMO Director',
    'Strategy Manager',
    'Strategy Specialist',
]

export function useMenuItems() {
    const { t } = useLanguageStore();
    const authStore = useAuthStore();

    const canManageUsersRoles = computed(() => {
        return authStore.hasAnyRole(['Super Admin', 'Admin'])
    });

    /**
     * Returns true if the user is allowed to see the given menu item.
     * Admin roles bypass permission checks, but explicit role-only items remain exact.
     */
    const canAccess = (item) => {
        if (!item) return false
        if (item.action) return true

        // Role gates describe identity, so an Admin must not satisfy a
        // Super-Admin-only item (or impersonate any other role).
        if (item.requiresRole) {
            const roles = Array.isArray(item.requiresRole) ? item.requiresRole : [item.requiresRole]
            if (!authStore.hasAnyRole(roles)) return false
        }

        // Both administrative roles have full feature permissions.
        if (canManageUsersRoles.value) return true

        // Deny-list: hide item if user has any of the blocked roles
        if (item.blockedRoles) {
            const blocked = Array.isArray(item.blockedRoles) ? item.blockedRoles : [item.blockedRoles]
            if (authStore.hasAnyRole(blocked)) return false
        }

        if (item.requiresPermission) {
            if (item.permissionFallbackRoles) {
                const roles = Array.isArray(item.permissionFallbackRoles) ? item.permissionFallbackRoles : [item.permissionFallbackRoles]
                if (authStore.hasAnyRole(roles)) return true
            }
            return Array.isArray(item.requiresPermission)
                ? authStore.hasAnyPermission(item.requiresPermission)
                : authStore.hasPermission(item.requiresPermission)
        }
        if (!item.to) return true
        const required = getRequiredPermissionFor({ path: item.to })
        if (!required) return true
        return Array.isArray(required)
            ? authStore.hasAnyPermission(required)
            : authStore.hasPermission(required)
    }

    /** Recursively prune menu items the user cannot access. */
    const filterTree = (items) => {
        return items
            .filter(item => !item.hidden)
            .filter(canAccess)
            .map(item => {
                if (Array.isArray(item.items) && item.items.length) {
                    const children = filterTree(item.items)
                    return { ...item, items: children }
                }
                return item
            })
            // Drop empty parent groups
            .filter(item => !Array.isArray(item.items) || item.items.length > 0 || item.to || item.action)
    }

    const menuItems = computed(() => filterTree([
        {
            label: t('Home'),
            icon: 'pi pi-fw pi-home',
            to: '/'
        },
        // {
        //     label: t('Dashboard'),
        //     icon: 'pi pi-fw pi-chart-bar',
        //     to: '/dashboard'
        // },
        {
            label: t('PMO Dashboard'),
            icon: 'pi pi-fw pi-chart-line',
            to: '/project/pmo-dashboard',
            blockedRoles: STRATEGY_ROLES,
            requiresPermission: ['pmo.dashboard', 'pmo.project-tracker', 'pmo.program-tracker', 'pmo.council-board'],
            permissionFallbackRoles: [
                'PMO',
                ...PROJECT_ROLES,
                ...PROGRAM_ROLES,
            ],
        },
        {
            label: t('PMO Library'),
            icon: 'pi pi-fw pi-folder-open',
            to: '/project/pmo-library',
        },
        {
            label: t('Organizational Structure'),
            icon: 'pi pi-fw pi-sitemap',
            to: '/project/organizational-structure',
            blockedRoles: STRATEGY_ROLES,
        },

        // {
        //     label: t('SMO Dashboard'),
        //     icon: 'pi pi-fw pi-chart-bar',
        //     to: '/strategy/smo-dashboard',
        //     blockedRoles: ['PMO', ...PROJECT_ROLES, ...PROGRAM_ROLES],
        //     requiresPermission: ['strategic.plans.read'],
        //     permissionFallbackRoles: STRATEGY_ROLES,
        // },
                {
            label: t('Initiatives Roadmap'),
            icon: 'pi pi-fw pi-calendar',
            to: '/initiatives-roadmap',
            blockedRoles: ['PMO', ...PROJECT_ROLES, ...PROGRAM_ROLES],
            requiresPermission: ['strategic.initiatives.read'],
            permissionFallbackRoles: STRATEGY_ROLES,
        },
        // {
        //     label: t('Strategic House'),
        //     icon: 'pi pi-fw pi-building-columns',
        //     to: '/strategic-house',
        //     blockedRoles: ['PMO', ...PROJECT_ROLES, ...PROGRAM_ROLES],
        // },
        //         {
        //     label: t('Strategy'),
        //     icon: 'pi pi-fw pi-compass',
        //     to: '/strategy/strategic-plans',
        //     blockedRoles: ['PMO', ...PROJECT_ROLES, ...PROGRAM_ROLES],
        //     requiresPermission: ['strategic.plans.read', 'strategic.plans.create', 'strategic.plans.update', 'strategic.plans.approve'],
        //     permissionFallbackRoles: STRATEGY_ROLES,
        // },
        {
            label: t('Projects'),
            icon: 'pi pi-fw pi-list',
            to: '/project/projects',
            blockedRoles: STRATEGY_ROLES,
        },
        {
            label: t('Tasks'),
            icon: 'pi pi-fw pi-check-square',
            to: '/project/tasks',
            hidden: true,
            blockedRoles: STRATEGY_ROLES,
            requiresPermission: 'projects.tasks.read',
        },
        {
            label: t('Programs'),
            icon: 'pi pi-fw pi-sitemap',
            to: '/project/programs',
            blockedRoles: ['PMO', ...STRATEGY_ROLES, ...PROJECT_ROLES],
        },
        {
            label: t('Portfolio'),
            icon: 'pi pi-fw pi-briefcase',
            to: '/portfolio',
            blockedRoles: STRATEGY_ROLES,
        },
        {
            label: t('Knowledge Hub'),
            icon: 'pi pi-fw pi-book',
            to: '/knowledge-hub',
            hidden: true,
        }

    ]));

    const bottomMenuItems = computed(() => filterTree([
        {
            label: t('Settings'),
            icon: 'pi pi-fw pi-cog',
            requiresRole: ['Super Admin', 'super admin', 'super_admin'],
            items: [
                {
                    label: t('Users'),
                    icon: 'pi pi-fw pi-user',
                    to: '/profile/list',
                    requiresPermission: 'users.read'
                },
                {
                    label: t('Roles'),
                    icon: 'pi pi-fw pi-shield',
                    to: '/roles',
                    requiresPermission: 'roles.read'
                },
                {
                    label: t('Permissions'),
                    icon: 'pi pi-fw pi-lock',
                    to: '/permissions',
                    requiresPermission: 'permissions.read'
                },
                {
                    label: t('Departments'),
                    icon: 'pi pi-fw pi-building',
                    to: '/departments',
                    requiresPermission: 'departments.read'
                },
                // {
                //     label: t('Data Import'),
                //     icon: 'pi pi-fw pi-upload',
                //     to: '/data-import',
                //     requiresRole: ['Super Admin', 'super admin', 'super_admin'],
                // },
            ]
        },
        {
            label: t('Logout'),
            icon: 'pi pi-fw pi-sign-out',
            action: 'logout'
        }
    ]));

    return { menuItems, bottomMenuItems };
}

// Keep backward compatibility with default export
const menu = [
    {
        label: 'Home',
        icon: 'pi pi-fw pi-home',
        to: '/'
    },
    {
        label: 'Dashboard',
        icon: 'pi pi-fw pi-chart-bar',
        to: '/dashboard'
    },
    {
        label: 'PMO Library',
        icon: 'pi pi-fw pi-folder-open',
        to: '/project/pmo-library'
    },
    {
        label: 'Organizational Structure',
        icon: 'pi pi-fw pi-sitemap',
        to: '/project/organizational-structure'
    },
    // Strategic House depends on Strategic Plans and is disabled with them.
    // {
    //     label: 'Strategic House',
    //     icon: 'pi pi-fw pi-building-columns',
    //     to: '/strategic-house'
    // },
    // Strategic Plans navigation is intentionally disabled for now.
    // {
    //     label: 'Strategy',
    //     icon: 'pi pi-fw pi-compass',
    //     to: '/strategy/strategic-plans'
    // },
    {
        label: 'Projects',
        icon: 'pi pi-fw pi-list',
        to: '/project/projects'
    },
    {
        label: 'Programs',
        icon: 'pi pi-fw pi-sitemap',
        to: '/project/programs'
    },
    {
        label: 'Knowledge Hub',
        icon: 'pi pi-fw pi-book',
        to: '/knowledge-hub'
    }
];

export const bottomMenu = [
    {
        label: 'Settings',
        icon: 'pi pi-fw pi-cog',
        requiresRole: ['Super Admin', 'super admin', 'super_admin'],
        items: [
            {
                label: 'Users',
                icon: 'pi pi-fw pi-user',
                to: '/profile/list'
            },
            {
                label: 'Roles',
                icon: 'pi pi-fw pi-shield',
                to: '/roles'
            },
            {
                label: 'Permissions',
                icon: 'pi pi-fw pi-lock',
                to: '/permissions'
            },
            {
                label: 'Departments',
                icon: 'pi pi-fw pi-building',
                to: '/departments'
            },
        ]
    },
    {
        label: 'Logout',
        icon: 'pi pi-fw pi-sign-out',
        action: 'logout'
    }
];

export default menu;
